import { useCallback, useMemo } from 'react';
import { endOfDay, format, isValid, parseISO, startOfDay } from 'date-fns';
import { resetPage, useUrlParams, writeParam } from './useUrlTableState';

/** How one filter value is read from and written to the URL query. */
export interface UrlCodec<T> {
  read: (params: URLSearchParams) => T | undefined;
  write: (params: URLSearchParams, value: T | undefined) => void;
}

export interface DateRangeValue {
  from?: Date;
  to?: Date;
}

const parseDay = (value: string | null): Date | undefined => {
  if (!value) {
    return undefined;
  }
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : undefined;
};

const toDay = (date: Date | undefined): string | undefined =>
  date ? format(date, 'yyyy-MM-dd') : undefined;

/**
 * A free text or id filter under one query param. Blank writes remove it.
 *
 * @example
 * urlString('merchant') // ?merchant=Billa
 */
export const urlString = (key: string): UrlCodec<string> => ({
  read: (params) => params.get(key) ?? undefined,
  write: (params, value) => writeParam(params, key, value?.trim()),
});

/**
 * A date range under two day params. Read back as full days, `to` at the end
 * of its day.
 *
 * @example
 * urlDateRange('bookedFrom', 'bookedTo') // ?bookedFrom=2026-09-01&bookedTo=2026-09-09
 */
export const urlDateRange = (
  fromKey: string,
  toKey: string
): UrlCodec<DateRangeValue> => ({
  read: (params) => {
    const from = parseDay(params.get(fromKey));
    const to = parseDay(params.get(toKey));
    return from || to
      ? { from: from && startOfDay(from), to: to && endOfDay(to) }
      : undefined;
  },
  write: (params, value) => {
    writeParam(params, fromKey, toDay(value?.from));
    writeParam(params, toKey, toDay(value?.to));
  },
});

type Codecs = Record<string, UrlCodec<any>>;
type CodecValue<C> = C extends UrlCodec<infer T> ? T : never;
export type UrlFilterValues<C extends Codecs> = {
  [K in keyof C]: CodecValue<C[K]> | undefined;
};

export interface UrlFilters<C extends Codecs> {
  values: UrlFilterValues<C>;
  /** Writes one filter and goes back to page 0, in one URL update. */
  set: <K extends keyof C>(key: K, value: UrlFilterValues<C>[K]) => void;
  clearAll: () => void;
  activeCount: number;
}

/**
 * Table filters kept in the URL query, declared once as a map of codecs. The
 * codec map must be stable, so define it at module level.
 *
 * @example
 * const CODECS = {
 *   bookedDate: urlDateRange('bookedFrom', 'bookedTo'),
 *   merchant: urlString('merchant'),
 * };
 * const { values, set, clearAll } = useUrlFilters(CODECS);
 * set('merchant', 'Billa'); // ?merchant=Billa
 */
export function useUrlFilters<C extends Codecs>(codecs: C): UrlFilters<C> {
  const { searchParams, update } = useUrlParams();

  const values = useMemo(() => {
    const result = {} as UrlFilterValues<C>;
    for (const key of Object.keys(codecs) as Array<keyof C>) {
      result[key] = codecs[key].read(searchParams);
    }
    return result;
  }, [codecs, searchParams]);

  const set = useCallback(
    <K extends keyof C>(key: K, value: UrlFilterValues<C>[K]) =>
      update((params) => {
        codecs[key].write(params, value);
        resetPage(params);
      }),
    [codecs, update]
  );

  const clearAll = useCallback(
    () =>
      update((params) => {
        Object.values(codecs).forEach((codec) =>
          codec.write(params, undefined)
        );
        resetPage(params);
      }),
    [codecs, update]
  );

  const activeCount = Object.values(values).filter(
    (value) => value !== undefined
  ).length;

  return useMemo(
    () => ({ values, set, clearAll, activeCount }),
    [values, set, clearAll, activeCount]
  );
}
